import React, { useState } from 'react';
import { Button } from '@/components/ui';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, CardExpandable } from '@/components/ui/Card/Card';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { useNotificationStore } from '@/stores/notificationStore';
import { CodeBlock } from '../CodeBlock';
import { SectionWrapper } from '../SectionWrapper';

export function CardSection() {
  const [cardExpanded, setCardExpanded] = useState(false);
  const notify = useNotificationStore(s => s.info);

  return (
    <SectionWrapper title="Card Components">
        <div>
          <h3 className="text-lg font-semibold mb-4">Basic Cards</h3>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 items-stretch">
            {/* Default Card */}
            <Card className="h-full">
              <CardHeader>
                <CardTitle>Default Card</CardTitle>
                <CardDescription>Standard card with border</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  This is the default card variant with a border and white background.
                  Notice the improved padding throughout the card.
                </p>
              </CardContent>
              <CardFooter>
                <Button size="sm">Action</Button>
              </CardFooter>
            </Card>

            {/* Elevated Card */}
            <Card variant="elevated" className="h-full">
              <CardHeader>
                <CardTitle>Elevated Card</CardTitle>
                <CardDescription>Card with shadow instead of border</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  This card uses a shadow for elevation without a border.
                </p>
              </CardContent>
              <CardFooter>
                <Button size="sm" variant="secondary">Action</Button>
              </CardFooter>
            </Card>

            {/* Bordered Card */}
            <Card variant="bordered" className="h-full">
              <CardHeader>
                <CardTitle>Bordered Card</CardTitle>
                <CardDescription>Transparent background with border</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  This card has a transparent background with just a border.
                </p>
              </CardContent>
              <CardFooter className="justify-end gap-2">
                <Button size="sm" variant="ghost">Cancel</Button>
                <Button size="sm" variant="outline">Save</Button>
              </CardFooter>
            </Card>
          </div>
          <CodeBlock>
{`<Card>
  <CardHeader>
    <CardTitle>Card Title</CardTitle>
    <CardDescription>Card description</CardDescription>
  </CardHeader>
  <CardContent>
    Content goes here
  </CardContent>
  <CardFooter>
    <Button>Action</Button>
  </CardFooter>
</Card>

<Card variant="elevated">
  <CardHeader>
    <CardTitle>Elevated Card</CardTitle>
  </CardHeader>
  <CardContent>
    Shadow instead of border
  </CardContent>
</Card>

<Card variant="bordered">
  <CardHeader>
    <CardTitle>Bordered Card</CardTitle>
  </CardHeader>
  <CardContent>
    Transparent with border
  </CardContent>
</Card>`}
          </CodeBlock>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Interactive Cards</h3>
          <div className="grid gap-6 md:grid-cols-2">
            <Card interactive onClick={() => notify('Card clicked!')}>
              <CardHeader>
                <CardTitle>Interactive Card</CardTitle>
                <CardDescription>Click me!</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  This card has hover effects and is clickable.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader onClick={() => setCardExpanded(!cardExpanded)} className="cursor-pointer">
                <div className="flex-1">
                  <CardTitle>Expandable Card</CardTitle>
                  <CardDescription>Click header to expand</CardDescription>
                </div>
                <ChevronDown className={`w-5 h-5 transition-transform ml-4 ${cardExpanded ? 'rotate-180' : ''}`} />
              </CardHeader>
              <CardExpandable isExpanded={cardExpanded}>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    This content is revealed with a smooth animation when the header is clicked.
                  </p>
                  <div className="mt-3 space-y-2">
                    <div className="h-2 bg-muted rounded"></div>
                    <div className="h-2 bg-muted rounded w-3/4"></div>
                    <div className="h-2 bg-muted rounded w-1/2"></div>
                  </div>
                </CardContent>
              </CardExpandable>
            </Card>
          </div>
          <CodeBlock>
{`<Card interactive onClick={() => alert('Clicked!')}>
  <CardHeader>
    <CardTitle>Interactive Card</CardTitle>
  </CardHeader>
  <CardContent>
    Clickable with hover effects
  </CardContent>
</Card>

const [expanded, setExpanded] = useState(false);

<Card>
  <CardHeader onClick={() => setExpanded(!expanded)}>
    <div className="flex-1">
      <CardTitle>Expandable Card</CardTitle>
      <CardDescription>Click to expand</CardDescription>
    </div>
    <ChevronIcon className={\`ml-4 \${expanded ? 'rotate-180' : ''}\`} />
  </CardHeader>
  <CardExpandable isExpanded={expanded}>
    <CardContent>
      Hidden content with animation
    </CardContent>
  </CardExpandable>
</Card>`}
          </CodeBlock>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Card with Header Actions</h3>
          <div className="max-w-2xl">
            <Card>
              <CardHeader>
                <div className="flex-1">
                  <CardTitle>User Settings</CardTitle>
                  <CardDescription>Manage user preferences and configurations</CardDescription>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Button size="sm" variant="ghost">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline">Edit</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium mb-1">Notification Settings</p>
                  <p className="text-sm text-muted-foreground">Email notifications are enabled</p>
                </div>
                <div>
                  <p className="text-sm font-medium mb-1">Privacy</p>
                  <p className="text-sm text-muted-foreground">Profile is visible to team members</p>
                </div>
              </CardContent>
              <CardFooter className="justify-between">
                <p className="text-xs text-muted-foreground">Last updated 2 hours ago</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost">Reset</Button>
                  <Button size="sm">Save Changes</Button>
                </div>
              </CardFooter>
            </Card>
          </div>
          <CodeBlock>
{`<Card>
  <CardHeader>
    <div className="flex-1">
      <CardTitle>User Settings</CardTitle>
      <CardDescription>Manage user preferences</CardDescription>
    </div>
    <div className="flex items-center gap-2 ml-4">
      <Button size="sm" variant="ghost">
        <RefreshIcon />
      </Button>
      <Button size="sm" variant="outline">Edit</Button>
    </div>
  </CardHeader>
  <CardContent>
    Content goes here
  </CardContent>
  <CardFooter className="justify-between">
    <p className="text-xs text-muted-foreground">Last updated 2 hours ago</p>
    <div className="flex gap-2">
      <Button size="sm" variant="ghost">Reset</Button>
      <Button size="sm">Save Changes</Button>
    </div>
  </CardFooter>
</Card>`}
          </CodeBlock>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Card Padding Options</h3>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card padding="none">
              <div className="p-4 border-b bg-muted">
                <CardTitle>No Padding</CardTitle>
              </div>
              <div className="p-4">
                <p className="text-sm">Custom padding control</p>
              </div>
            </Card>

            <Card padding="sm">
              <CardHeader>
                <CardTitle>Small</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">16px padding</p>
              </CardContent>
            </Card>

            <Card padding="md">
              <CardHeader>
                <CardTitle>Medium</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">24px padding (default)</p>
              </CardContent>
            </Card>

            <Card padding="lg">
              <CardHeader>
                <CardTitle>Large</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">32px padding</p>
              </CardContent>
            </Card>
          </div>
          <CodeBlock>
{`<Card padding="none">     // No padding
<Card padding="sm">       // 16px padding
<Card padding="md">       // 24px padding (default)
<Card padding="lg">       // 32px padding`}
          </CodeBlock>
        </div>
    </SectionWrapper>
  );
}
